import { useContext, useEffect, useState } from "react";
import { Localization, SessionContext } from "@viamap/viamap2-common";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { useANNEditorContext } from "./ANNEditorState";
import { AnnotationContextType } from "./ANNTypes";
import { ANNManager } from "./ANNManager";

/**
 * Shows annotation count for a servitut tingbog row (same documentId as servitut details / ANN table).
 * Renders nothing while loading, on error, when the module is off, or when count is 0.
 */
export function ServitutAnnotationCountBadge(props: { documentId: string }) {
   const { documentId } = props;
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const { state: sessionState } = useContext(SessionContext);
   const { state: editorState } = useANNEditorContext();
   const [count, setCount] = useState<number | null>(null);
   const organizationId = sessionState?.customerRef;
   const moduleOn = hasAccessToFeature(Feature.AnnotationModule);

   useEffect(() => {
      if (!documentId || !moduleOn) {
         setCount(null);
         return;
      }
      let cancelled = false;
      setCount(null);
      void (async () => {
         try {
            const n = await ANNManager.countServitutAnnotations(documentId, organizationId);
            if (!cancelled) {
               setCount(n);
            }
         } catch {
            if (!cancelled) {
               setCount(null);
            }
         }
      })();
      return () => {
         cancelled = true;
      };
   }, [documentId, organizationId, moduleOn]);

   useEffect(() => {
      if (!editorState.lastSaveTimestamp) {
         return;
      }
      if (editorState.contextType !== AnnotationContextType.Servitut) {
         return;
      }
      if (String(editorState.identifier ?? "") !== documentId) {
         return;
      }
      if (!documentId || !moduleOn) {
         return;
      }
      let cancelled = false;
      const t = setTimeout(() => {
         void (async () => {
            try {
               const n = await ANNManager.countServitutAnnotations(documentId, organizationId);
               if (!cancelled) {
                  setCount(n);
               }
            } catch {
               if (!cancelled) {
                  setCount(null);
               }
            }
         })();
      }, 800);
      return () => {
         cancelled = true;
         clearTimeout(t);
      };
   }, [
      editorState.lastSaveTimestamp,
      editorState.contextType,
      editorState.identifier,
      documentId,
      organizationId,
      moduleOn,
   ]);

   if (!documentId || !moduleOn) {
      return null;
   }
   if (count === null || count < 1) {
      return null;
   }

   const title = `${count} ${Localization.getText("Annotations")}`;
   return (
      <span className="tingbog-servitut-annotation-count" title={title} aria-label={title}>
         {count}
      </span>
   );
}
